import React, { useState, useEffect } from 'react';
import { Input } from '../Input';
import { Select } from '../Select';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { colors } from '../../theme/colors';
import {
    fetchBoatBrands,
    fetchEngineBrands,
    fetchBoatModels,
    fetchEngineModels,
    BoatBrand,
    EngineBrand,
    BoatModel,
    EngineModel,
} from '../../lib/api/dataClient';

// --- Boat Brand Selector (Chips + Dropdown Fallback) ---
interface BoatBrandSelectorProps {
    label: string;
    popularBrands: BoatBrand[];
    allBrands: BoatBrand[];
    selectedId: number | null;
    onSelect: (brandId: number) => void;
    disabled?: boolean;
}

const BoatBrandSelector: React.FC<BoatBrandSelectorProps> = ({
    label,
    popularBrands,
    allBrands,
    selectedId,
    onSelect,
    disabled = false,
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [hoveredChip, setHoveredChip] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Filter brands for search
    const filteredBrands = allBrands.filter(b =>
        b.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const labelStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.text.primary,
        marginBottom: spacing[12],
        display: 'block',
    };

    const chipsContainerStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: spacing[8],
        marginBottom: spacing[12],
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    };

    const getChipStyle = (brandId: number): React.CSSProperties => {
        const isSelected = selectedId === brandId;
        const isHovered = hoveredChip === brandId;

        return {
            padding: `${spacing[8]} ${spacing[16]}`,
            borderRadius: '24px',
            fontFamily: typography.fontFamily,
            fontSize: typography.sizes.body.fontSize,
            fontWeight: typography.weights.medium,
            cursor: 'pointer',
            transition: 'border 120ms ease-out, background-color 150ms ease-out, color 150ms ease-out, box-shadow 150ms ease-out',
            border: isSelected
                ? `2px solid ${colors.primary.navy}`
                : `1px solid ${isHovered ? 'rgba(10, 38, 71, 0.3)' : 'rgba(10, 38, 71, 0.15)'}`,
            backgroundColor: isSelected ? colors.primary.navy : '#FFFFFF',
            color: isSelected ? '#FFFFFF' : colors.text.primary,
            boxShadow: isSelected
                ? '0 0 0 3px rgba(201, 179, 126, 0.25)'
                : 'none',
            outline: 'none',
        };
    };

    const actionButtonStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        padding: `${spacing[8]} 0`,
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.text.muted,
        cursor: 'pointer',
        transition: 'color 150ms ease-out',
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    };

    const containerStyle: React.CSSProperties = {
        marginBottom: spacing[24],
    };

    return (
        <div style={containerStyle}>
            <label style={labelStyle}>{label}</label>

            {/* Popular Brand Chips */}
            <div style={chipsContainerStyle}>
                {popularBrands.map((brand) => (
                    <button
                        key={brand.id}
                        type="button"
                        style={getChipStyle(brand.id)}
                        onClick={() => onSelect(brand.id)}
                        onMouseEnter={() => setHoveredChip(brand.id)}
                        onMouseLeave={() => setHoveredChip(null)}
                        disabled={disabled}
                    >
                        {brand.name}
                    </button>
                ))}
            </div>

            {/* Show Dropdown Action */}
            <button
                type="button"
                style={{
                    ...actionButtonStyle,
                    color: showDropdown ? colors.primary.navy : colors.text.muted,
                }}
                onClick={() => setShowDropdown(!showDropdown)}
                onMouseEnter={(e) => (e.currentTarget.style.color = colors.primary.navy)}
                onMouseLeave={(e) => (e.currentTarget.style.color = showDropdown ? colors.primary.navy : colors.text.muted)}
                disabled={disabled}
            >
                {showDropdown ? 'Piilota lista' : 'Eikö löydy merkkiä?'}
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                        transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 180ms ease-out',
                    }}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {/* Searchable Dropdown (hidden by default) */}
            {showDropdown && (
                <div style={{
                    marginTop: spacing[12],
                    animation: 'expandDropdown 180ms ease-out',
                }}>
                    <style>{`
                        @keyframes expandDropdown {
                            from {
                                opacity: 0.9;
                                transform: translateY(-4px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                    `}</style>
                    {/* Search input */}
                    <Input
                        label=""
                        placeholder="Hae merkkiä..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {/* Filtered brand list */}
                    <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: spacing[8],
                        border: '1px solid rgba(10, 38, 71, 0.15)',
                        borderRadius: '8px',
                    }}>
                        {filteredBrands.map((brand) => (
                            <button
                                key={brand.id}
                                type="button"
                                onClick={() => {
                                    onSelect(brand.id);
                                    setShowDropdown(false);
                                    setSearchTerm('');
                                }}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: `${spacing[12]} ${spacing[16]}`,
                                    border: 'none',
                                    background: selectedId === brand.id ? colors.background.offWhite : '#FFFFFF',
                                    textAlign: 'left',
                                    fontFamily: typography.fontFamily,
                                    fontSize: typography.sizes.body.fontSize,
                                    color: colors.text.primary,
                                    cursor: 'pointer',
                                    borderBottom: '1px solid rgba(10, 38, 71, 0.08)',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.background.offWhite}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedId === brand.id ? colors.background.offWhite : '#FFFFFF'}
                            >
                                {brand.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Engine Brand Selector (Chips Only, No Dropdown) ---
interface EngineBrandSelectorProps {
    label: string;
    brands: EngineBrand[];
    selectedId: number | null;
    onSelect: (brandId: number) => void;
    disabled?: boolean;
}

const EngineBrandSelector: React.FC<EngineBrandSelectorProps> = ({
    label,
    brands,
    selectedId,
    onSelect,
    disabled = false,
}) => {
    const [hoveredChip, setHoveredChip] = useState<number | null>(null);

    const labelStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.text.primary,
        marginBottom: spacing[12],
        display: 'block',
    };

    const chipsContainerStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: spacing[8],
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    };

    const getChipStyle = (brandId: number): React.CSSProperties => {
        const isSelected = selectedId === brandId;
        const isHovered = hoveredChip === brandId;

        return {
            padding: `${spacing[8]} ${spacing[16]}`,
            borderRadius: '24px',
            fontFamily: typography.fontFamily,
            fontSize: typography.sizes.body.fontSize,
            fontWeight: typography.weights.medium,
            cursor: 'pointer',
            transition: 'border 120ms ease-out, background-color 150ms ease-out, color 150ms ease-out, box-shadow 150ms ease-out',
            border: isSelected
                ? `2px solid ${colors.primary.navy}`
                : `1px solid ${isHovered ? 'rgba(10, 38, 71, 0.3)' : 'rgba(10, 38, 71, 0.15)'}`,
            backgroundColor: isSelected ? colors.primary.navy : '#FFFFFF',
            color: isSelected ? '#FFFFFF' : colors.text.primary,
            boxShadow: isSelected
                ? '0 0 0 3px rgba(201, 179, 126, 0.25)'
                : 'none',
            outline: 'none',
        };
    };

    const containerStyle: React.CSSProperties = {
        marginBottom: spacing[24],
    };

    return (
        <div style={containerStyle}>
            <label style={labelStyle}>{label}</label>
            <div style={chipsContainerStyle}>
                {brands.map((brand) => (
                    <button
                        key={brand.id}
                        type="button"
                        style={getChipStyle(brand.id)}
                        onClick={() => onSelect(brand.id)}
                        onMouseEnter={() => setHoveredChip(brand.id)}
                        onMouseLeave={() => setHoveredChip(null)}
                        disabled={disabled}
                    >
                        {brand.name}
                    </button>
                ))}
            </div>
        </div>
    );
};

// --- Main Step1 Component ---
interface Step1Props {
    boatData: any; // Using loose type or WizardState parts
    engineData: any;
    onDataChange: (boat: any, engine: any) => void;
}

export const Step1: React.FC<Step1Props> = ({
    boatData,
    engineData,
    onDataChange,
}) => {
    // Current valid IDs derived from props string data
    // We assume data.brand is the ID string
    const boatBrandId = boatData.brand ? parseInt(boatData.brand) : null;
    const boatModelId = boatData.model ? parseInt(boatData.model) : null;
    const engineBrandId = engineData.brand ? parseInt(engineData.brand) : null;
    const engineModelId = engineData.model ? parseInt(engineData.model) : null;

    // Data states
    const [boatBrands, setBoatBrands] = useState<BoatBrand[]>([]);
    const [engineBrands, setEngineBrands] = useState<EngineBrand[]>([]);
    const [boatModels, setBoatModels] = useState<BoatModel[]>([]);
    const [engineModels, setEngineModels] = useState<EngineModel[]>([]);

    // Loading states
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [boatModelsLoading, setBoatModelsLoading] = useState(false);
    const [engineModelsLoading, setEngineModelsLoading] = useState(false);

    // Error states
    const [brandsError, setBrandsError] = useState<string | null>(null);
    const [boatModelsError, setBoatModelsError] = useState<string | null>(null);
    const [engineModelsError, setEngineModelsError] = useState<string | null>(null);

    // Helpers to update parent
    const updateBoat = (updates: any) => onDataChange({ ...boatData, ...updates }, engineData);
    const updateEngine = (updates: any) => onDataChange(boatData, { ...engineData, ...updates });

    // Fetch brands on mount
    useEffect(() => {
        const loadBrands = async () => {
            setBrandsLoading(true);
            setBrandsError(null);
            try {
                const [boatBrandsData, engineBrandsData] = await Promise.all([
                    fetchBoatBrands(),
                    fetchEngineBrands(),
                ]);
                setBoatBrands(boatBrandsData);
                setEngineBrands(engineBrandsData);
            } catch (error) {
                console.error('Failed to fetch brands:', error);
                setBrandsError('Merkkien lataaminen epäonnistui.');
            } finally {
                setBrandsLoading(false);
            }
        };

        loadBrands();
    }, []);

    // Fetch boat models when boat brand is selected
    useEffect(() => {
        if (!boatBrandId || !Number.isFinite(boatBrandId) || boatBrandId <= 0) {
            setBoatModels([]);
            return;
        }

        const loadBoatModels = async () => {
            setBoatModelsLoading(true);
            setBoatModelsError(null);
            try {
                const models = await fetchBoatModels(boatBrandId);
                setBoatModels(models);
            } catch (error) {
                console.error('Failed to fetch boat models:', error);
                setBoatModelsError('Mallien lataaminen epäonnistui.');
            } finally {
                setBoatModelsLoading(false);
            }
        };

        loadBoatModels();
    }, [boatBrandId]);

    // Fetch engine models when engine brand is selected
    useEffect(() => {
        if (!engineBrandId || !Number.isFinite(engineBrandId) || engineBrandId <= 0) {
            setEngineModels([]);
            return;
        }

        const loadEngineModels = async () => {
            setEngineModelsLoading(true);
            setEngineModelsError(null);
            try {
                const models = await fetchEngineModels(engineBrandId);
                setEngineModels(models);
            } catch (error) {
                console.error('Failed to fetch engine models:', error);
                setEngineModelsError('Mallien lataaminen epäonnistui.');
            } finally {
                setEngineModelsLoading(false);
            }
        };

        loadEngineModels();
    }, [engineBrandId]);

    // Handlers
    const handleBoatBrandSelect = (brandId: number) => {
        const brand = boatBrands.find(b => b.id === brandId);
        if (brand) {
            // Reset model
            updateBoat({
                brand: brandId.toString(),
                brandName: brand.name,
                model: undefined,
                modelName: undefined
            });
        }
    };

    const handleEngineBrandSelect = (brandId: number) => {
        const brand = engineBrands.find(b => b.id === brandId);
        if (brand) {
            // Reset model
            updateEngine({
                brand: brandId.toString(),
                brandName: brand.name,
                model: undefined,
                modelName: undefined
            });
        }
    };

    const handleBoatModelSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const modelId = parseInt(e.target.value, 10);
        const model = boatModels.find(m => m.id === modelId);
        if (model) {
            updateBoat({
                model: modelId.toString(),
                modelName: model.name || model.label
            });
        }
    };

    const handleEngineModelSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const modelId = parseInt(e.target.value, 10);
        const model = engineModels.find(m => m.id === modelId);
        if (model) {
            updateEngine({
                model: modelId.toString(),
                modelName: model.name || model.label
            });
        }
    };

    // Popular boat brand names
    const POPULAR_BOAT_BRAND_NAMES = ['Buster', 'Yamarin', 'Finnmaster', 'Silver', 'Terhi', 'Suvi', 'Bella'];
    const popularBoatBrands: BoatBrand[] = boatBrands.filter(b =>
        POPULAR_BOAT_BRAND_NAMES.some(name => name.toLowerCase() === b.name.toLowerCase())
    );
    const allBoatBrands: BoatBrand[] = boatBrands;
    const engineBrandOptions: EngineBrand[] = engineBrands;

    const boatModelOptions = boatModels.map(m => ({ label: m.label || m.name || `Model ${m.id}`, value: String(m.id) }));
    const engineModelOptions = engineModels.map(m => ({ label: m.label || m.name || `Model ${m.id}`, value: String(m.id) }));

    const titleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '28px',
        fontWeight: typography.weights.bold,
        color: colors.text.primary,
        marginBottom: spacing[12],
        marginTop: 0,
    };

    const helperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        color: colors.text.muted,
        marginBottom: spacing[48],
        lineHeight: 1.6,
    };

    const sectionTitleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        fontWeight: typography.weights.semiBold,
        color: colors.text.primary,
        marginBottom: spacing[24],
        marginTop: spacing[48],
    };

    const gridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: spacing[24],
    };

    const errorStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        color: '#DC2626',
        marginBottom: spacing[16],
        padding: spacing[12],
        backgroundColor: '#FEE2E2',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };

    const retryButtonStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        padding: `${spacing[4]} ${spacing[8]}`,
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.primary.navy,
        cursor: 'pointer',
        textDecoration: 'underline',
        outline: 'none',
    };

    return (
        <div>
            <h2 style={titleStyle}>Kerro meille veneestäsi ja moottorista</h2>
            <p style={helperStyle}>
                Täytä tiedot niin tarkasti kuin tiedät.
            </p>

            {brandsError && (
                <div style={errorStyle}>
                    <span>{brandsError}</span>
                    <button type="button" style={retryButtonStyle} onClick={() => window.location.reload()}>
                        Yritä uudelleen
                    </button>
                </div>
            )}

            <h3 style={sectionTitleStyle}>Veneen tiedot</h3>
            <div style={{ marginBottom: spacing[24] }}>
                <Input
                    label="Rekisterinumero (valinnainen)"
                    placeholder="Esim. A12345"
                    value={boatData.registration || ''}
                    onChange={(e) => updateBoat({ registration: e.target.value })}
                />
            </div>

            <BoatBrandSelector
                label="Veneen merkki"
                popularBrands={popularBoatBrands}
                allBrands={allBoatBrands}
                selectedId={boatBrandId}
                onSelect={handleBoatBrandSelect}
                disabled={brandsLoading}
            />

            {boatModelsError && (
                <div style={{ ...errorStyle, marginBottom: spacing[12] }}>
                    <span>{boatModelsError}</span>
                </div>
            )}

            <div style={gridStyle}>
                <Select
                    label="Veneen malli"
                    placeholder={boatModelsLoading ? "Ladataan..." : "Valitse malli"}
                    options={boatModelOptions}
                    value={boatModelId?.toString() || ''}
                    onChange={handleBoatModelSelect}
                    disabled={!boatBrandId || boatModelsLoading}
                />
            </div>

            <h3 style={sectionTitleStyle}>Moottorin tiedot</h3>

            <EngineBrandSelector
                label="Moottorin merkki"
                brands={engineBrandOptions}
                selectedId={engineBrandId}
                onSelect={handleEngineBrandSelect}
                disabled={brandsLoading}
            />

            {engineModelsError && (
                <div style={{ ...errorStyle, marginBottom: spacing[12] }}>
                    <span>{engineModelsError}</span>
                </div>
            )}

            <div style={gridStyle}>
                <Select
                    label="Moottorin malli"
                    placeholder={engineModelsLoading ? "Ladataan..." : "Valitse malli"}
                    options={engineModelOptions}
                    value={engineModelId?.toString() || ''}
                    onChange={handleEngineModelSelect}
                    disabled={!engineBrandId || engineModelsLoading}
                />
                <Input
                    label="Vuosimalli (valinnainen)"
                    placeholder="VVVV"
                    style={{ width: '100%' }}
                    value={engineData.year || ''}
                    onChange={(e) => updateEngine({ year: e.target.value })}
                />
                <Input
                    label="Teho (hv) (valinnainen)"
                    placeholder="Esim. 150"
                    style={{ width: '100%' }}
                    value={engineData.horsepower || ''}
                    onChange={(e) => updateEngine({ horsepower: e.target.value })}
                />
                <div style={{ gridColumn: '1 / -1' }}>
                    <Input
                        label="Sarjanumero (valinnainen)"
                        placeholder="Kirjoita sarjanumero"
                        value={engineData.serialNumber || ''}
                        onChange={(e) => updateEngine({ serialNumber: e.target.value })}
                    />
                </div>
            </div>
        </div>
    );
};

